import React, { useState, useRef, useEffect } from 'react';
import { 
  ArrowLeft, 
  Play, 
  Pause, 
  Volume2, 
  Maximize2,
  FileText,
  Mail,
  Video,
  Twitter,
  Instagram,
  Sparkles,
  Quote,
  CheckCircle,
  Clock
} from 'lucide-react';
import '../../styles/studio-design-system.css';

interface TranscriptLine {
  timestamp: string;
  speaker: string;
  text: string;
  startTime: number;
  endTime: number;
}

interface SocialClip {
  id: string;
  title: string;
  duration: string;
  thumbnail: string;
  selected: boolean;
  startTime: number;
  endTime: number;
}

interface BlogPost {
  title: string;
  content: string;
  wordCount: number;
}

interface Newsletter {
  subject: string;
  content: string;
  wordCount: number;
}

interface SocialPost {
  platform: string;
  content: string;
  characterCount: number;
}

interface StudioEditorProps {
  onBack?: () => void;
}

const StudioEditor: React.FC<StudioEditorProps> = ({ onBack }) => {
  const [activeTab, setActiveTab] = useState('blog');
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [selectedText, setSelectedText] = useState('');
  const [showMagicMenu, setShowMagicMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const [selectedTranscriptLine, setSelectedTranscriptLine] = useState<number | null>(null);
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const transcriptRef = useRef<HTMLDivElement>(null);

  // Mock data
  const [transcript] = useState<TranscriptLine[]>([
    { timestamp: '00:01', speaker: 'Sarah', text: 'Thanks for joining me today, Dr. Chen. I\'m really excited to dive into this topic.', startTime: 1, endTime: 5 },
    { timestamp: '00:05', speaker: 'Dr. Chen', text: 'It\'s great to be here, Sarah. This is such an important conversation we need to have.', startTime: 5, endTime: 9 },
    { timestamp: '00:09', speaker: 'Sarah', text: 'Absolutely. Let\'s start with the big picture. What do you see as the most critical challenge facing healthcare today?', startTime: 9, endTime: 15 },
    { timestamp: '00:15', speaker: 'Dr. Chen', text: 'The biggest challenge is definitely access. We have incredible technology and treatments, but they\'re not reaching the people who need them most.', startTime: 15, endTime: 22 },
    { timestamp: '00:22', speaker: 'Dr. Chen', text: 'This is the key point that keeps me up at night - how do we democratize healthcare innovation?', startTime: 22, endTime: 28 },
    { timestamp: '00:28', speaker: 'Sarah', text: 'That\'s a powerful statement. Can you elaborate on what you mean by democratizing healthcare innovation?', startTime: 28, endTime: 33 }
  ]);

  const [socialClips] = useState<SocialClip[]>([
    { id: '1', title: 'The biggest challenge is access', duration: '0:15', thumbnail: '/api/placeholder/200/120', selected: true, startTime: 15, endTime: 30 },
    { id: '2', title: 'Democratizing healthcare innovation', duration: '0:12', thumbnail: '/api/placeholder/200/120', selected: true, startTime: 22, endTime: 34 },
    { id: '3', title: 'Technology not reaching people', duration: '0:18', thumbnail: '/api/placeholder/200/120', selected: false, startTime: 15, endTime: 33 },
    { id: '4', title: 'What keeps me up at night', duration: '0:10', thumbnail: '/api/placeholder/200/120', selected: true, startTime: 22, endTime: 32 },
    { id: '5', title: 'Critical challenge facing healthcare', duration: '0:14', thumbnail: '/api/placeholder/200/120', selected: false, startTime: 9, endTime: 23 }
  ]);

  const [blogPost] = useState<BlogPost>({
    title: 'The Future of Healthcare: A Conversation with Dr. Sarah Chen',
    content: `# The Future of Healthcare: A Conversation with Dr. Sarah Chen

In a world where medical technology advances at breakneck speed, one critical question remains: How do we ensure these innovations reach everyone who needs them?

Dr. Sarah Chen, a leading healthcare innovator, recently sat down with us to discuss the most pressing challenges facing the industry today. Her insights reveal a fundamental gap between what's possible and what's accessible.

## The Access Problem

"The biggest challenge is definitely access," Dr. Chen explains. "We have incredible technology and treatments, but they're not reaching the people who need them most."

This statement hits at the heart of healthcare inequality. While breakthrough treatments emerge regularly, they often remain confined to well-funded research institutions and affluent communities.

## Democratizing Healthcare Innovation

Dr. Chen's vision extends beyond traditional healthcare delivery. "This is the key point that keeps me up at night - how do we democratize healthcare innovation?"

The solution, she suggests, lies in reimagining how we distribute medical knowledge and resources. It's not just about creating new treatments; it's about ensuring they reach every corner of society.

## The Path Forward

As we look to the future, the question isn't whether we can develop better treatments - it's whether we can make them universally accessible. Dr. Chen's insights point to a healthcare system that serves everyone, not just those who can afford it.

The conversation continues, and the stakes couldn't be higher.`,
    wordCount: 2150
  });

  const [newsletter] = useState<Newsletter>({
    subject: 'The Healthcare Access Crisis: What Dr. Sarah Chen Wants You to Know',
    content: `## The Healthcare Access Crisis: What Dr. Sarah Chen Wants You to Know

In this week's conversation, Dr. Sarah Chen reveals the biggest challenge facing healthcare today - and it's not what you might think.

**The Problem:** We have incredible medical technology, but it's not reaching the people who need it most.

**The Solution:** Democratizing healthcare innovation to ensure everyone benefits from medical advances.

**Key Quote:** "This is the key point that keeps me up at night - how do we democratize healthcare innovation?"

Dr. Chen's insights challenge us to rethink how we distribute medical knowledge and resources. It's not just about creating new treatments; it's about ensuring they reach every corner of society.

**What This Means for You:** The future of healthcare depends on making innovations accessible to everyone, not just those who can afford them.

Read the full conversation and join the discussion about creating a more equitable healthcare system.`,
    wordCount: 450
  });

  const [socialPosts] = useState<SocialPost[]>([
    { platform: 'Twitter', content: 'Thread: The biggest challenge in healthcare isn\'t technology - it\'s access. Dr. Sarah Chen explains why democratizing innovation is the key to solving our healthcare crisis. 🧵', characterCount: 180 },
    { platform: 'LinkedIn', content: 'The healthcare access crisis: We have incredible medical technology, but it\'s not reaching the people who need it most. Dr. Sarah Chen shares her insights on democratizing healthcare innovation.', characterCount: 195 },
    { platform: 'Instagram', content: 'The future of healthcare depends on making innovations accessible to everyone. Dr. Sarah Chen explains why this is the challenge that keeps her up at night. #HealthcareInnovation #AccessForAll', characterCount: 165 }
  ]);

  const handleTextSelection = (e: React.MouseEvent) => {
    const selection = window.getSelection();
    if (selection && selection.toString().length > 0) {
      setSelectedText(selection.toString());
      setMenuPosition({ x: e.clientX, y: e.clientY });
      setShowMagicMenu(true);
    }
  };

  const handleMagicAction = (action: string) => {
    console.log(`Magic action: ${action} on text: "${selectedText}"`);
    setShowMagicMenu(false);
    setSelectedText('');
  };

  const handleTranscriptClick = (line: TranscriptLine, index: number) => {
    setSelectedTranscriptLine(index);
    if (videoRef.current) {
      videoRef.current.currentTime = line.startTime;
    }
  };

  const toggleClipSelection = (clipId: string) => {
    // Toggle clip selection logic
    console.log(`Toggle clip selection: ${clipId}`);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'blog':
        return (
          <div>
            <div className="mb-4">
              <input 
                type="text" 
                value={blogPost.title}
                className="w-full text-2xl font-semibold border-none outline-none bg-transparent"
                onChange={(e) => console.log('Title changed:', e.target.value)}
              />
              <div className="text-subtle text-sm mt-1">
                {blogPost.wordCount} words
              </div>
            </div>
            <div 
              className="prose max-w-none"
              onMouseUp={handleTextSelection}
            >
              {blogPost.content.split('\n').map((paragraph, index) => (
                <p key={index} className="mb-4">{paragraph}</p>
              ))}
            </div>
          </div>
        );
      
      case 'newsletter':
        return (
          <div>
            <div className="mb-4">
              <input 
                type="text" 
                value={newsletter.subject}
                className="w-full text-xl font-semibold border-none outline-none bg-transparent"
                onChange={(e) => console.log('Subject changed:', e.target.value)}
              />
              <div className="text-subtle text-sm mt-1">
                {newsletter.wordCount} words
              </div>
            </div>
            <div 
              className="prose max-w-none"
              onMouseUp={handleTextSelection}
            >
              {newsletter.content.split('\n').map((paragraph, index) => (
                <p key={index} className="mb-4">{paragraph}</p>
              ))}
            </div>
          </div>
        );
      
      case 'clips':
        return (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Social Clips ({socialClips.length})</h3>
              <div className="text-subtle text-sm">
                {socialClips.filter(clip => clip.selected).length} selected
              </div>
            </div>
            <div className="asset-grid">
              {socialClips.map((clip) => (
                <div 
                  key={clip.id} 
                  className={`asset-card ${clip.selected ? 'selected' : ''}`}
                  onClick={() => toggleClipSelection(clip.id)}
                >
                  <div className="aspect-video bg-gray-200 rounded mb-2 flex items-center justify-center">
                    <Play className="w-8 h-8 text-gray-400" />
                  </div>
                  <div className="text-sm font-medium mb-1">{clip.title}</div>
                  <div className="text-subtle text-xs">{clip.duration}</div>
                  {clip.selected && (
                    <div className="flex items-center gap-1 mt-2 text-green-600 text-xs">
                      <CheckCircle className="w-3 h-3" />
                      Selected
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      
      case 'social':
        return (
          <div>
            <h3 className="text-lg font-semibold mb-4">Social Posts</h3>
            <div className="space-y-4">
              {socialPosts.map((post, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    {post.platform === 'Twitter' && <Twitter className="w-4 h-4 text-blue-400" />}
                    {post.platform === 'LinkedIn' && <div className="w-4 h-4 bg-blue-600 rounded"></div>}
                    {post.platform === 'Instagram' && <Instagram className="w-4 h-4 text-pink-500" />}
                    <span className="font-medium">{post.platform}</span>
                    <span className="text-subtle text-sm">({post.characterCount} characters)</span>
                  </div>
                  <textarea 
                    value={post.content}
                    className="w-full border-none outline-none resize-none"
                    rows={3}
                    onChange={(e) => console.log('Social post changed:', e.target.value)}
                  />
                </div>
              ))}
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen focus-mode">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-600">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                className="btn-secondary flex items-center gap-2"
                onClick={onBack}
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Home
              </button>
              <div>
                <input 
                  type="text" 
                  value="Interview with Dr. Sarah Chen"
                  className="text-xl font-semibold border-none outline-none bg-transparent"
                  onChange={(e) => console.log('Project title changed:', e.target.value)}
                />
              </div>
            </div>
            <button className="btn-primary">
              Publish...
            </button>
          </div>
        </div>
      </div>

      {/* Split Screen */}
      <div className="split-screen">
        {/* Left Pane - Source */}
        <div className="split-left">
          <div className="mb-4">
            <div className="aspect-video video-player rounded-lg mb-4 flex items-center justify-center">
              <div className="text-center text-white">
                <Play className="w-12 h-12 mx-auto mb-2" />
                <p>Video Player</p>
                <p className="text-sm text-gray-300">45:30 duration</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button 
                className="btn-secondary"
                onClick={() => setIsPlaying(!isPlaying)}
              >
                {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              </button>
              <div className="flex-1 bg-gray-200 rounded-full h-2">
                <div className="bg-blue-600 h-2 rounded-full" style={{ width: '25%' }}></div>
              </div>
              <span className="text-sm text-gray-600">11:30 / 45:30</span>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-3">Transcript</h3>
            <div className="transcript" ref={transcriptRef}>
              {transcript.map((line, index) => (
                <div 
                  key={index}
                  className={`transcript-line ${selectedTranscriptLine === index ? 'selected' : ''}`}
                  onClick={() => handleTranscriptClick(line, index)}
                >
                  <span className="timestamp">{line.timestamp}</span>
                  <span className="font-medium">{line.speaker}:</span>
                  <span className="ml-2">{line.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Pane - Assets */}
        <div className="split-right">
          <div className="tab-container">
            <ul className="tab-list">
              <li 
                className={`tab-item ${activeTab === 'blog' ? 'active' : ''}`}
                onClick={() => setActiveTab('blog')}
              >
                <FileText className="w-4 h-4 mr-2" />
                Blog Post
              </li>
              <li 
                className={`tab-item ${activeTab === 'newsletter' ? 'active' : ''}`}
                onClick={() => setActiveTab('newsletter')}
              >
                <Mail className="w-4 h-4 mr-2" />
                Newsletter
              </li>
              <li 
                className={`tab-item ${activeTab === 'clips' ? 'active' : ''}`}
                onClick={() => setActiveTab('clips')}
              >
                <Video className="w-4 h-4 mr-2" />
                Clips (5)
              </li>
              <li 
                className={`tab-item ${activeTab === 'social' ? 'active' : ''}`}
                onClick={() => setActiveTab('social')}
              >
                <Twitter className="w-4 h-4 mr-2" />
                Social Posts
              </li>
            </ul>
          </div>

          <div className="flex-1 overflow-y-auto">
            {renderTabContent()}
          </div>
        </div>
      </div>

      {/* Magic Menu */}
      {showMagicMenu && (
        <div 
          className="magic-menu"
          style={{ 
            position: 'fixed', 
            left: menuPosition.x, 
            top: menuPosition.y - 50,
            zIndex: 1000
          }}
        >
          <div 
            className="magic-menu-item"
            onClick={() => handleMagicAction('create-clip')}
          >
            <Sparkles className="w-4 h-4" />
            Create Social Clip
          </div>
          <div 
            className="magic-menu-item"
            onClick={() => handleMagicAction('add-quote')}
          >
            <Quote className="w-4 h-4" />
            Add as Pull-Quote
          </div>
          <div 
            className="magic-menu-item"
            onClick={() => handleMagicAction('create-tweet')}
          >
            <Twitter className="w-4 h-4" />
            Create Tweet
          </div>
        </div>
      )}

      {/* Click outside to close magic menu */}
      {showMagicMenu && (
        <div 
          className="fixed inset-0 z-999"
          onClick={() => setShowMagicMenu(false)}
        />
      )}
    </div>
  );
};

export default StudioEditor;
